/**
 * Citizen status lookup by tracking code.
 *
 * Returns PROGRESS ONLY — the state and the date it was submitted. Never which
 * officer holds it, never their name, never any note they wrote. Two reasons:
 *
 *   1. It would tell anyone holding the code which officer to approach, and in
 *      a water-theft dispute that officer may already be under pressure.
 *   2. The lookup is unauthenticated by necessity, so whatever it returns is
 *      returned to whoever has the code — including someone who obtained it
 *      from the person who filed the report.
 *
 * The code itself is stored only as a SHA-256 hash, so this endpoint can
 * confirm a code matches a report and nothing can go the other way: there is no
 * query that turns a report back into a person.
 */

import { useState } from 'react';
import { useTranslation } from 'react-i18next';

type Result =
  | { kind: 'idle' }
  | { kind: 'searching' }
  | { kind: 'found'; state: string; submittedAt: string }
  | { kind: 'notFound' }
  | { kind: 'error' };

export function TrackStatus() {
  const { t, i18n } = useTranslation();
  const [code, setCode] = useState('');
  const [result, setResult] = useState<Result>({ kind: 'idle' });

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    setResult({ kind: 'searching' });
    try {
      const res = await fetch('/api/report-status', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ trackingCode: code.trim() }),
      });
      if (res.status === 404) {
        setResult({ kind: 'notFound' });
        return;
      }
      if (!res.ok) {
        setResult({ kind: 'error' });
        return;
      }
      const body = (await res.json()) as { state: string; submittedAt: string };
      setResult({ kind: 'found', state: body.state, submittedAt: body.submittedAt });
    } catch {
      setResult({ kind: 'error' });
    }
  }

  return (
    <div className="flex flex-col gap-6">
      <div>
        <h1 className="text-2xl font-semibold">{t('track.heading')}</h1>
        <p className="mt-2" style={{ color: 'var(--text-secondary)' }}>
          {t('track.intro')}
        </p>
      </div>

      <form
        onSubmit={(e) => void onSubmit(e)}
        className="rounded-lg p-5"
        style={{ background: 'var(--bg-surface)', border: '1px solid var(--border-subtle)' }}
      >
        <label className="block text-sm font-medium" htmlFor="code">
          {t('track.codeLabel')}
        </label>
        <input
          id="code"
          value={code}
          onChange={(e) => setCode(e.target.value.toUpperCase())}
          placeholder="ABCD-EFGH-JKLM"
          dir="ltr"
          autoComplete="off"
          spellCheck={false}
          className="mt-1 w-full rounded-md px-3 py-2 tracking-widest"
          style={{
            background: 'var(--bg-elevated)',
            color: 'var(--text-primary)',
            border: '1px solid var(--border-subtle)',
          }}
        />
        <button
          type="submit"
          disabled={result.kind === 'searching' || code.trim().length < 6}
          className="mt-4 rounded-md px-5 py-3 font-medium disabled:opacity-60"
          style={{ background: 'var(--accent)', color: '#fff' }}
        >
          {result.kind === 'searching' ? t('track.searching') : t('track.search')}
        </button>
      </form>

      <div aria-live="polite">
        {result.kind === 'found' && (
          <div
            className="rounded-lg p-5"
            style={{ background: 'var(--bg-surface)', border: '1px solid var(--success)' }}
          >
            <p className="text-xs uppercase tracking-wide" style={{ color: 'var(--text-muted)' }}>
              {t('track.statusLabel')}
            </p>
            <p className="mt-1 text-lg font-semibold">{t(`reportState.${result.state}`)}</p>
            <p className="mt-3 text-sm" style={{ color: 'var(--text-secondary)' }}>
              {t('track.submittedOn', {
                date: new Intl.DateTimeFormat(i18n.language === 'ur' ? 'ur-PK' : 'en-GB', {
                  dateStyle: 'long',
                }).format(new Date(result.submittedAt)),
              })}
            </p>
            <p className="mt-3 text-xs" style={{ color: 'var(--text-muted)' }}>
              {t('track.noOfficerDetails')}
            </p>
          </div>
        )}

        {result.kind === 'notFound' && (
          <p
            className="rounded-lg p-4 text-sm"
            style={{ background: 'var(--bg-surface)', border: '1px solid var(--warning)' }}
          >
            {t('track.notFound')}
          </p>
        )}

        {result.kind === 'error' && (
          <p
            className="rounded-lg p-4 text-sm"
            style={{ background: 'var(--bg-surface)', border: '1px solid var(--danger)' }}
          >
            {t('track.serverError')}
          </p>
        )}
      </div>
    </div>
  );
}
